import { call, put, takeLatest, all } from 'redux-saga/effects';
import {
    FETCH_FEE_TYPE_NAMES_START,
    FetchFeeTypeNamesStartAction,
    fetchFeeTypeNamesSuccess,
    fetchFeeTypeNamesFailed,
    FETCH_FEE_TYPE_EXAMPLE_START,
    FetchFeeTypeExampleStartAction,
    fetchFeeTypeExampleSuccess,
    fetchFeeTypeExampleFailed,
    SAVE_FEE_TYPE_EXAMPLE_START,
    SaveFeeTypeExampleStartAction,
    saveFeeTypeExampleSuccess,
    saveFeeTypeExampleFailed,
    CREATE_FEE_TYPE_EXAMPLE_START,
    CreateFeeTypeExampleStartAction,
    createFeeTypeExampleSuccess,
    createFeeTypeExampleFailed,
    DELETE_FEE_TYPE_EXAMPLE_START,
    DeleteFeeTypeExampleStartAction,
    deleteFeeTypeExampleSuccess,
    deleteFeeTypeExampleFailed,
    FETCH_FEE_TYPE_EXAMPLE_VERSIONS_START,
    FetchFeeTypeExampleVersionsStartAction,
    fetchFeeTypeExampleVersionsSuccess,
    fetchFeeTypeExampleVersionsFailed,
    ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START,
    ActivateFeeTypeExampleVersionStartAction,
    activateFeeTypeExampleVersionSuccess,
    activateFeeTypeExampleVersionFailed,
    fetchFeeTypeNamesStart
} from "src/actions/FeeTypeExampleActions";
import FeeTypeExampleApiUtil from "src/utils/FeeTypeExampleApiUtil";

// Fetch Fee Type Names Saga
export function* fetchFeeTypeNames(action: FetchFeeTypeNamesStartAction): Generator<any, void, any> {
    try {
        const response = yield call(FeeTypeExampleApiUtil.getFeeTypeExampleKeys);
        yield put(fetchFeeTypeNamesSuccess(response));
    } catch (error) {
        console.error("Fetch Fee Type Names error:", error);
        yield put(fetchFeeTypeNamesFailed());
    }
}

// Fetch Fee Type Example Saga
export function* fetchFeeTypeExample(action: FetchFeeTypeExampleStartAction): Generator<any, void, any> {
    try {
        console.log("Fetching fee type example for:", action.feeType);
        const response = yield call(FeeTypeExampleApiUtil.getFeeTypeExample, action.feeType);
        console.log("Fee type example response:", response);
        if (response) {
            yield put(fetchFeeTypeExampleSuccess(response));
        } else {
            console.log("No fee type example found for:", action.feeType);
            yield put(fetchFeeTypeExampleFailed());
        }
    } catch (error) {
        console.error("Fetch Fee Type Example error:", error);
        yield put(fetchFeeTypeExampleFailed());
    }
}

// Save Fee Type Example Saga
export function* saveFeeTypeExample(action: SaveFeeTypeExampleStartAction): Generator<any, void, any> {
    try {
        const response = yield call(FeeTypeExampleApiUtil.updateFeeTypeExample, action.request);
        yield put(saveFeeTypeExampleSuccess(response));
    } catch (error) {
        console.error("Save Fee Type Example error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to save fee type example";
        yield put(saveFeeTypeExampleFailed(errorMessage));
    }
}

// Create Fee Type Example Saga
export function* createFeeTypeExample(action: CreateFeeTypeExampleStartAction): Generator<any, void, any> {
    try {
        const response = yield call(FeeTypeExampleApiUtil.createFeeTypeExample, action.request);
        yield put(createFeeTypeExampleSuccess(response));
        
        // Refresh the fee type names list after successful creation
        try {
            const keysResponse = yield call(FeeTypeExampleApiUtil.getFeeTypeExampleKeys);
            yield put(fetchFeeTypeNamesSuccess(keysResponse));
        } catch (keysError) {
            console.error("Failed to refresh fee type names after creation:", keysError);
            // Don't fail the entire operation if refreshing keys fails
        }
    } catch (error) {
        console.error("Create Fee Type Example error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to create fee type example";
        yield put(createFeeTypeExampleFailed(errorMessage));
    }
}

// Delete Fee Type Example Saga
export function* deleteFeeTypeExample(action: DeleteFeeTypeExampleStartAction): Generator<any, void, any> {
    try {
        yield call(FeeTypeExampleApiUtil.deleteFeeTypeExample, action.feeType);
        yield put(deleteFeeTypeExampleSuccess());
        
        // Refresh the fee type names list after successful deletion
        try {
            const keysResponse = yield call(FeeTypeExampleApiUtil.getFeeTypeExampleKeys);
            yield put(fetchFeeTypeNamesSuccess(keysResponse));
        } catch (keysError) {
            console.error("Failed to refresh fee type names after deletion:", keysError);
            // Don't fail the entire operation if refreshing keys fails
        }
    } catch (error) {
        console.error("Delete Fee Type Example error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to delete fee type example";
        yield put(deleteFeeTypeExampleFailed(errorMessage));
    }
}

// Fetch Fee Type Example Versions Saga
export function* fetchFeeTypeExampleVersions(action: FetchFeeTypeExampleVersionsStartAction): Generator<any, void, any> {
    try {
        console.log("Fetching fee type example versions for:", action.feeType);
        const response = yield call(FeeTypeExampleApiUtil.getFeeTypeExampleVersions, action.feeType);
        console.log("Fee type example versions response:", response);
        yield put(fetchFeeTypeExampleVersionsSuccess(response));
    } catch (error) {
        console.error("Fetch Fee Type Example Versions error:", error);
        yield put(fetchFeeTypeExampleVersionsFailed());
    }
}

// Activate Fee Type Example Version Saga
export function* activateFeeTypeExampleVersion(action: ActivateFeeTypeExampleVersionStartAction): Generator<any, void, any> {
    try {
        console.log("Activating fee type example version:", action.request.versionId, "for:", action.request.feeType);
        const response = yield call(FeeTypeExampleApiUtil.activateFeeTypeExampleVersion, action.request);
        yield put(activateFeeTypeExampleVersionSuccess(response));
    } catch (error) {
        console.error("Activate Fee Type Example Version error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to activate version";
        yield put(activateFeeTypeExampleVersionFailed(errorMessage));
    }
}

// Root Saga for Fee Type Names
export function* feeTypeNamesSaga(): Generator<any, void, any> {
    yield takeLatest(FETCH_FEE_TYPE_NAMES_START, fetchFeeTypeNames);
}

// Root Saga for Fee Type Example Query
export function* FeeTypeExampleQuerySaga(): Generator<any, void, any> {
    yield takeLatest(FETCH_FEE_TYPE_EXAMPLE_START, fetchFeeTypeExample);
}

// Root Saga for Fee Type Example Save
export function* FeeTypeExampleSaveSaga(): Generator<any, void, any> {
    yield takeLatest(SAVE_FEE_TYPE_EXAMPLE_START, saveFeeTypeExample);
}

// Root Saga for Fee Type Example Create
export function* FeeTypeExampleCreateSaga(): Generator<any, void, any> {
    yield takeLatest(CREATE_FEE_TYPE_EXAMPLE_START, createFeeTypeExample);
}

// Root Saga for Fee Type Example Delete
export function* FeeTypeExampleDeleteSaga(): Generator<any, void, any> {
    yield takeLatest(DELETE_FEE_TYPE_EXAMPLE_START, deleteFeeTypeExample);
}

// Root Saga for Fee Type Example Versions
export function* FeeTypeExampleVersionsSaga(): Generator<any, void, any> {
    yield takeLatest(FETCH_FEE_TYPE_EXAMPLE_VERSIONS_START, fetchFeeTypeExampleVersions);
}

// Root Saga for Activate Version
export function* ActivateVersionSaga(): Generator<any, void, any> {
    yield takeLatest(ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START, activateFeeTypeExampleVersion);
}

// Combined Fee Type Example Sagas
export default function* FeeTypeExampleSagas(): Generator<any, void, any> {
    yield all([
        feeTypeNamesSaga(),
        FeeTypeExampleQuerySaga(),
        FeeTypeExampleSaveSaga(),
        FeeTypeExampleCreateSaga(),
        FeeTypeExampleDeleteSaga(),
        FeeTypeExampleVersionsSaga(),
        ActivateVersionSaga()
    ]);
}
